import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Row, Col, DropdownMenu, DropdownItem } from 'reactstrap';

import useDateLocale from 'services/translations/useDateLocale';
import PageContainer from 'components/layout/PageContainer';
import PrimaryButtonDropdown from 'components/layout/buttons/PrimaryButtonDropdown';
import IOfferGet from 'api/interfaces/offers/IOfferGet';
import {
  NDA_MASKED_VALUE,
  NDA_STATE_NONE,
  NDA_STATE_RESTRICTED,
} from 'services/enums/offers/ndaProtections';

import { fetchOfferTypes, fetchStatusTypes } from 'store/offers/actions';
import BasicMaterialTable from '../layout/tables/BasicMaterialTable';

import apiOffers from '../../api/offers';
import pdfIcon from '../../assets/pdf_icon.svg';
import OfferStatusOptions from './src/offers/OfferStatusOptions';
import orderOfferStatuses from './src/offers/orderOfferStatuses';
import OfferTypeOptions from './src/offers/OfferTypeOptions';
import orderOfferTypes from './src/offers/orderOfferTypes';
import translations from '../../services/translations/translations';

import 'style/offers/Offers.css';
import 'style/offers/OfferNda.css';
import openOfferPdf from './services/openOfferPdf';
import { fetchUsers } from '../../store/users/actions';
import IUserGet from '../../api/interfaces/users/IUserGet';

const Offers = ({
  offerStatusesList,
  fetchOfferStatusesList,
  fetchUsersList,
  offerTypesList,
  usersList,
  fetchOfferTypesList,
}: {
  offerStatusesList: string[];
  fetchOfferStatusesList: () => void;
  fetchUsersList: () => void;
  offerTypesList: string[];
  usersList: IUserGet[];
  fetchOfferTypesList: () => void;
}): JSX.Element | null => {
  const history = useHistory();
  const { t } = useTranslation([
    translations.offers,
    translations.common,
    translations.projects,
  ]);
  const dateLocale = useDateLocale();
  const [sizePerPage, setSizePerPage] = useState(50);
  const [selectedRow, setSelectedRow] = useState<any>();
  const OFFERS_FILTER_SESSION = 'offers-filter-local-storage';

  useEffect(() => {
    fetchOfferStatusesList();
    fetchOfferTypesList();
    fetchUsersList();
  }, []);

  useEffect(() => {
    if (selectedRow !== undefined) {
      history.push(`/offers/${selectedRow?.id}/${selectedRow?.type}`);
    }
  }, [history, selectedRow, selectedRow]);

  const getColumnFieldFromFilter = (query: any, fieldName: string): any => {
    return query.filters?.find((f: any) => f.column.field === fieldName)?.value;
  };

  const getOffers = async (query: any) => {
    try {
      const created = getColumnFieldFromFilter(query, 'created');
      const updated = getColumnFieldFromFilter(query, 'updated');
      const createdUserId = getColumnFieldFromFilter(query, 'createdUserId');
      const updatedUserId = getColumnFieldFromFilter(query, 'updatedUserId');
      const search = getColumnFieldFromFilter(query, 'targetName');
      // Customer and recipient are searched with any part of the name, like the project
      const customer = getColumnFieldFromFilter(query, 'customerName');
      const recipient = getColumnFieldFromFilter(query, 'recipientName');
      const type = getColumnFieldFromFilter(query, 'type');
      const status = getColumnFieldFromFilter(query, 'status');

      const list = await apiOffers.get({
        created: created || null,
        createdUserId: createdUserId?.join(','),
        customer,
        limit: query.pageSize,
        offset: query.page * query.pageSize,
        search,
        orderBy: query.orderBy?.field,
        orderByDirection: query.orderDirection || null,
        recipient,
        updated,
        updatedUserId: updatedUserId?.join(','),
        type: type?.join(','),
        status: status?.join(','),
      });

      const count = await apiOffers.count({
        created: created || null,
        createdUserId: createdUserId?.join(','),
        customer,
        search,
        orderBy: query.orderBy?.field,
        orderByDirection: query.orderDirection || null,
        recipient,
        updated,
        updatedUserId: updatedUserId?.join(','),
        type: type?.join(','),
        status: status?.join(','),
      });

      return {
        data: list,
        page: query.page,
        totalCount: count.count,
      };
    } catch (error) {
      return {};
    }
  };

  const colTitles = {
    type: t(`${translations.offers}:Offer Type`),
    customer: t(`${translations.offers}:Customer Office`),
    recipient: t(`${translations.offers}:Recipient`),
    targetName: t(`${translations.offers}:Project`),
    status: t(`${translations.projects}:status.Status`),
    created: t(`${translations.common}:Created`),
    createdBy: t(`${translations.common}:CreatedBy`),
    updated: t(`${translations.common}:Updated`),
    updatedBy: t(`${translations.common}:UpdatedBy`),
    pdf: '',
  };

  const handlePdfOpenEvent = async (e: any, row: any) => {
    e.preventDefault();
    e.stopPropagation();
    return openOfferPdf(row.id, row.type);
  };

  const imageFormatter = (cell: any, row: any) => (
    <img
      id={row.targetName}
      alt="pdf icon"
      src={pdfIcon}
      onClick={(e) => handlePdfOpenEvent(e, cell)}
    />
  );

  const typeFormatter = (cell: any, row: any) =>
    t(`${translations.offers}:types:${cell}`);

  const statusFormatter = (cell: any, row: any) =>
    t(`${translations.offers}:statuses:${cell}`);

  usersList.sort((u1, u2) => {
    const n1 = (u1.firstName + u1.lastName).toLowerCase();
    const n2 = (u2.firstName + u2.lastName).toLowerCase();
    return n1.localeCompare(n2);
  });

  const usersFilter: any = {};

  usersList.forEach((u) => {
    const key = u.id ? u.id : '';
    if (u.id == null) {
      return false;
    }
    usersFilter[key] = `${u.firstName} ${u.lastName}`;
    return true;
  });

  const getDefaultFilter = (field: string) => {
    const storedFilters = window.localStorage.getItem(OFFERS_FILTER_SESSION);
    if (storedFilters == null) {
      return null;
    }

    const lastUsedFilters = JSON.parse(storedFilters);
    if (lastUsedFilters == null || lastUsedFilters.length <= 0) {
      return null;
    }

    const result = lastUsedFilters.find(
      (lastUsedfilter: any) => lastUsedfilter.column.field === field
    );

    return result != null ? result.value : null;
  };

  const columns = [
    {
      field: 'targetName',
      title: colTitles.targetName,
      defaultFilter: getDefaultFilter('targetName'),
      render: (row: IOfferGet) => (
        <>
          {row.targetName === NDA_MASKED_VALUE
            ? t(`${translations.offers}:nda.Protected`)
            : row.targetName}
          {(row.ndaState ?? NDA_STATE_NONE) !== NDA_STATE_NONE ? (
            <span
              className={`offer-nda-badge ${
                row.ndaState === NDA_STATE_RESTRICTED ? 'restricted' : ''
              }`}
            >
              NDA
            </span>
          ) : null}
        </>
      ),
      cellStyle: {
        width: 310,
        minWidth: 310,
      },
      headerStyle: {
        width: 310,
        minWidth: 310,
      },
    },
    {
      field: 'revisionNumber',
      title: t(`${translations.offers}:Revision`),
      filtering: false,
      sorting: false,
      render: (cell: any) => cell.revisionNumber ?? 1,
    },
    {
      field: 'customerName',
      title: colTitles.customer,
      defaultFilter: getDefaultFilter('customerName'),
      // An offer sent to several companies shows the first one and the number of others
      render: (row: any) => {
        if (
          row.ndaState === NDA_STATE_RESTRICTED &&
          row.customerName === NDA_MASKED_VALUE
        ) {
          return t(`${translations.offers}:nda.Protected`);
        }

        return row.customerCount > 1
          ? `${row.customerName ?? ''} (+${row.customerCount - 1})`
          : row.customerName;
      },
      cellStyle: {
        minWidth: 220,
      },
      headerStyle: {
        minWidth: 220,
      },
    },
    {
      field: 'recipientName',
      title: colTitles.recipient,
      defaultFilter: getDefaultFilter('recipientName'),
      cellStyle: {
        minWidth: 200,
      },
      headerStyle: {
        minWidth: 200,
      },
    },
    {
      field: 'type',
      title: colTitles.type,
      defaultFilter: getDefaultFilter('type'),
      lookup: t(`${translations.offers}:types`, { returnObjects: true }),
      render: (cell: any) => typeFormatter(cell.type, 'type'),
    },
    {
      field: 'status',
      title: colTitles.status,
      defaultFilter: getDefaultFilter('status'),
      lookup: t(`${translations.offers}:statuses`, { returnObjects: true }),
      render: (cell: any) => statusFormatter(cell.status, 'status'),
    },
    {
      field: 'created',
      title: colTitles.created,
      defaultFilter: getDefaultFilter('created'),
      type: 'date',
      dateSetting: { locale: dateLocale.intl },
    },
    {
      field: 'createdUserId',
      title: colTitles.createdBy,
      defaultFilter: getDefaultFilter('createdUserId'),
      lookup: usersFilter,
    },
    {
      field: 'updated',
      title: colTitles.updated,
      defaultFilter: getDefaultFilter('updated'),
      type: 'date',
      dateSetting: { locale: dateLocale.intl },
    },
    {
      field: 'updatedUserId',
      title: colTitles.updatedBy,
      defaultFilter: getDefaultFilter('updatedUserId'),
      lookup: usersFilter,
    },
    {
      field: 'pdf',
      title: colTitles.pdf,
      filtering: false,
      render: (cell: any) => imageFormatter(cell, 'pdf'),
    },
  ];

  const getOfferTypes = () => {
    if (offerTypesList.length !== 0) {
      return (
        <DropdownMenu right className="primary-dropdown-menu">
          {orderOfferTypes(offerTypesList).map((type, i) => {
            return (
              <DropdownItem
                onClick={() => history.push(`/offers/types/${i}/create`)}
                key={Math.random()}
              >
                {t(`${translations.offers}:types:${type}`).toUpperCase()}
              </DropdownItem>
            );
          })}
        </DropdownMenu>
      );
    }
    return <div />;
  };

  return (
    <PageContainer fullWidth>
      <Row className="row-content">
        <Col xs={9}>
          <p className="header-text">{t(`${translations.common}:Offers`)}</p>
        </Col>
        <Col xs={3} className="header-add-btn">
          <PrimaryButtonDropdown
            className="add-button"
            text={t(`${translations.common}:Add`)}
          >
            {getOfferTypes()}
          </PrimaryButtonDropdown>
        </Col>
      </Row>
      <div className="basic-table table-background-offers">
        <Row>
          <Col>
            <BasicMaterialTable
              columns={columns}
              fetchData={getOffers}
              sizePerPage={sizePerPage}
              setSelectedRow={setSelectedRow}
              localStorageTag={OFFERS_FILTER_SESSION}
            />
          </Col>
        </Row>
      </div>
    </PageContainer>
  );
};

const mapStateToProps = (state: any) => ({
  offerTypesList: state.offers.offerTypes,
  offerStatusesList: state.offers.statusTypes,
  usersList: state.users.users,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchOfferTypesList: () => dispatch(fetchOfferTypes()),
  fetchOfferStatusesList: () => dispatch(fetchStatusTypes()),
  fetchUsersList: () => dispatch(fetchUsers()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Offers);
